import React from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import {
  buildSchemaDefaults,
  resolveLabelFormatting,
  foldResolvedIntoLabelsSet
} from '../pedigree-tool/pedigree/resolveLabelFormatting';

// TGCLOUD-1663 — fetching the scoped presets and applying the default stack.
//
// This used to be a MOUNT SIDE EFFECT of LabelConfigurations, which lived
// inside the always-mounted sidebar drawer. That is load-bearing behaviour: the
// user's default preset is applied to the chart whether or not they ever open
// the panel. Moving the configuration UI into an on-demand modal would have
// silently stopped presets applying at all — the chart would quietly fall back
// to schema defaults for everybody.
//
// So the fetch-and-apply lives here, called once per chart from PedigreeBox
// (beside the usergroup displayLabelMode default), and the modal consumes the
// same fetched data rather than fetching again.

export const pickDefault = (arr) =>
  (arr || []).find((p) => p.isDefault) || null;

// Resolve a preset stack onto a labelsSet: sparse per-label patches through the
// resolver, then the most-specific preset's `settings` blob wholesale on top.
// Order and value-sort travel in `settings` for exactly that reason — they are
// whole-collection properties that must not be half-inherited.
export const applyPresetStack = (labelsSet, { system, group, user }) => {
  const resolved = resolveLabelFormatting(
    buildSchemaDefaults(),
    system,
    group,
    user
  );
  const folded = foldResolvedIntoLabelsSet(resolved, labelsSet);
  const top = user || group || system;
  return { ...folded, ...(top?.settings || {}) };
};

/**
 * Load the user's label presets and apply the default stack to the chart once.
 *
 * @param {(updater: Function|Object) => void} setLabelsSet
 * @param {boolean} enabled — false while the chart has nothing to apply to
 */
export const useLabelPresets = (setLabelsSet, enabled = true) => {
  const [scoped, setScoped] = React.useState({
    system: [],
    group: [],
    user: []
  });
  const [loadError, setLoadError] = React.useState(null);
  // WHICH preset is applied, not just "did I apply one". The modal needs it to
  // offer "update this preset" and to show whether the chart still matches it,
  // and it has to outlive the modal — which unmounts on close — so it is owned
  // here beside labelsSet rather than inside the dialog.
  const [appliedPresetId, setAppliedPresetId] = React.useState(null);
  const appliedRef = React.useRef(false);

  const load = React.useCallback(
    ({ apply } = { apply: false }) =>
      axios
        .get(APICONSTANTS.labelDisplayPresets)
        .then(({ data }) => {
          const next = {
            system: data?.system || [],
            group: data?.group || [],
            user: data?.user || []
          };
          setScoped(next);
          setLoadError(null);
          if (apply && !appliedRef.current) {
            appliedRef.current = true;
            const stack = {
              system: pickDefault(next.system),
              group: pickDefault(next.group),
              user: pickDefault(next.user)
            };
            // Nothing marked default at any scope means nothing to apply —
            // leave the chart on its schema defaults rather than forcing a
            // preset the user never chose.
            if (stack.system || stack.group || stack.user) {
              setLabelsSet((prev) => applyPresetStack(prev, stack));
              // Most specific wins, mirroring applyPresetStack's own `top`.
              const top = stack.user || stack.group || stack.system;
              if (top?._id) setAppliedPresetId(top._id);
            }
          }
          return next;
        })
        .catch((err) => {
          // Previously a bare `.catch(() => {})`: a failed fetch silently
          // became "schema defaults" with no clue why the user's saved preset
          // had not applied. Surfacing it is the point of relocating this.
          setLoadError(err);
          return null;
        }),
    [setLabelsSet]
  );

  React.useEffect(() => {
    if (!enabled) return;
    load({ apply: true });
  }, [enabled, load]);

  return {
    scoped,
    loadError,
    reload: load,
    appliedPresetId,
    setAppliedPresetId
  };
};
